"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter, useSearchParams } from "next/navigation";
import { useClientSidebar } from "@/components/sidebar/client-sidebar-context";
import { icons } from "@/components/sidebar/nav-icons";
import { useTour } from "@/components/tour/tour-context";
import { useRecentClients } from "@/hooks/use-recent-clients";
import { useRecentSearches } from "@/hooks/use-recent-searches";
import { anonymizeClientName } from "@/lib/demo-mode";
import { DOC_TOPICS } from "@/lib/docs/registry";
import { useDebouncedAction } from "@/hooks/use-debounced-action";
import { globalSearchAction } from "@/lib/actions/search";
import { loadClientList, type ClientListEntry } from "@/lib/clients/client-list-cache";
import { MIN_ENTITY_QUERY } from "@/lib/search/entities";
import { highlight } from "@/lib/search/match";
import { COMPACT_NAV_MEDIA_QUERY, DESKTOP_NAV_MEDIA_QUERY } from "@/lib/nav/responsive";
import {
  searchAgencyPages,
  searchClientPages,
  searchCompanies,
  searchDocTopics,
  isStaff as isStaffRole,
  type SearchContext,
} from "@/lib/search/sources";
import type { SearchCategory, SearchFilters, SearchResult, SearchSection } from "@/lib/search/types";
import { useSearchShortcut } from "./use-search-shortcut";

const MAX_PER_SECTION = 5;
const MIN_QUERY = 1;
const CATEGORY_FILTERS: Array<{ value: SearchCategory | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "navigate", label: "Places" },
  { value: "work", label: "Work" },
  { value: "content", label: "Content" },
  { value: "data", label: "Data" },
  { value: "people", label: "People" },
  { value: "help", label: "Help" },
];

interface GlobalSearchProps {
  role: string;
}

/**
 * App-wide search. A dropdown anchored under the input rather than a centred
 * modal, so the bar stays visible and the result list reads as part of the
 * chrome.
 *
 * Two tiers. Nav destinations, companies and help topics are already in the
 * browser, so they filter instantly on every keystroke. Indexed record content
 * comes from globalSearchAction, debounced, and merges in when it lands rather
 * than holding up the instant half.
 */
export function GlobalSearch({ role }: GlobalSearchProps) {
  const { client, demoMode, setMobileNavOpen } = useClientSidebar();
  const { active: tourActive } = useTour();
  const { recentClients } = useRecentClients();
  const { recentSearches, recordSearch } = useRecentSearches();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState<ClientListEntry[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [category, setCategory] = useState<SearchCategory | "all">("all");
  const [currentCompanyOnly, setCurrentCompanyOnly] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);

  const displayName = useCallback(
    (name: string) => (demoMode ? anonymizeClientName(name) : name),
    [demoMode],
  );

  // Range and compare follow you to a company page, the same way the switcher
  // carries them, so searching does not silently reset the period on screen.
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    const range = searchParams.get("range");
    const compare = searchParams.get("compare");
    if (range) params.set("range", range);
    if (compare) params.set("compare", compare);
    return params.toString();
  }, [searchParams]);

  // Entity search is the one part that costs a round trip, so it is debounced
  // and kept separate from the instant client-side sections. The server action
  // uses deterministic records in demo mode and never reads the real corpus.
  const entitySearch = useDebouncedAction(
    useCallback(
      (arg: { query: string; clientId: string | null; filters: SearchFilters }) =>
        globalSearchAction(arg.query, arg.clientId, arg.filters),
      [],
    ),
  );
  const { trigger: triggerEntities, reset: resetEntities } = entitySearch;

  const runEntitySearch = useCallback(
    (next: string, nextFilters?: SearchFilters) => {
      const trimmed = next.trim();
      if (trimmed.length < MIN_ENTITY_QUERY) {
        resetEntities();
        return;
      }
      triggerEntities({
        query: trimmed,
        clientId: client?.clientId ?? null,
        filters: nextFilters ?? { category, currentCompanyOnly },
      });
    },
    [client?.clientId, category, currentCompanyOnly, triggerEntities, resetEntities],
  );

  const openSearch = useCallback(() => {
    const compact = window.matchMedia(COMPACT_NAV_MEDIA_QUERY).matches;
    setCompactMode(compact);
    if (compact) setMobileNavOpen(false);
    setOpen(true);
    void loadClientList().then(setCompanies).catch(() => setCompanies([]));
  }, [setMobileNavOpen]);

  const closeSearch = useCallback(() => {
    const restoreMobileFocus = compactMode;
    setOpen(false);
    setCompactMode(false);
    setQuery("");
    setActiveIndex(0);
    setCategory("all");
    setCurrentCompanyOnly(false);
    // Drop any in-flight entity request so its response cannot repopulate the
    // panel after it has closed.
    resetEntities();
    if (restoreMobileFocus) requestAnimationFrame(() => mobileTriggerRef.current?.focus());
  }, [compactMode, resetEntities]);

  useSearchShortcut(openSearch, tourActive);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) closeSearch();
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, closeSearch]);

  useEffect(() => {
    if (!open || !compactMode) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeSearch();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    function onDesktopChange(event: MediaQueryListEvent) {
      if (event.matches) closeSearch();
    }
    const desktopQuery = window.matchMedia(DESKTOP_NAV_MEDIA_QUERY);
    desktopQuery.addEventListener("change", onDesktopChange);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      desktopQuery.removeEventListener("change", onDesktopChange);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, compactMode, closeSearch]);

  const ctx: SearchContext = useMemo(
    () => ({
      role,
      demoMode,
      client: client
        ? {
            clientId: client.clientId,
            clientName: client.clientName,
            providers: client.providers,
            hiddenViews: client.hiddenViews,
            permissions: client.permissions,
            customDashboards: client.customDashboards,
            socialEnabled: client.socialEnabled,
            sitemapsEnabled: client.sitemapsEnabled,
          }
        : null,
    }),
    [role, demoMode, client],
  );

  const sections: SearchSection[] = useMemo(() => {
    const trimmed = query.trim();
    const all = companies ?? [];

    // Empty state: recents plus where you already are, so the panel is useful
    // before a single keystroke.
    if (trimmed.length < MIN_QUERY) {
      const byId = new Map(all.map((c) => [c.id, c]));
      const recents = recentClients
        .flatMap((r) => {
          const match = byId.get(r.id);
          return match && match.id !== client?.clientId ? [match] : [];
        })
        .slice(0, MAX_PER_SECTION)
        .map<SearchResult>((c) => ({
          id: `company:${c.id}`,
          kind: "company",
          category: "navigate",
          clientId: c.id,
          title: displayName(c.name),
          subtitle: "Company",
          href: queryParams ? `/clients/${c.id}?${queryParams}` : `/clients/${c.id}`,
          iconKey: "grid",
          matches: [],
          score: 0,
        }));
      const searchRows = recentSearches.slice(0, MAX_PER_SECTION).map<SearchResult>((term) => ({
        id: `recent-search:${term}`,
        kind: "page",
        title: term,
        subtitle: "Recent search",
        href: "",
        iconKey: "search",
        matches: [],
        score: 0,
        queryTerm: term,
      }));

      return [
        ...(searchRows.length > 0
          ? [{ key: "recent-searches", label: "Recent searches", results: searchRows }]
          : []),
        ...(recents.length > 0
          ? [{ key: "recent", label: "Recent companies", results: recents }]
          : []),
      ];
    }

    const show = (target: SearchCategory) => category === "all" || category === target;
    // Apply the selected filters locally as well as on the server request. This
    // keeps the previous debounced response from flashing after a filter click.
    const entitySections = (entitySearch.data?.sections ?? []).flatMap((section) => {
      const results = section.results.filter((result) => {
        if (category !== "all" && result.category !== category) return false;
        if (currentCompanyOnly && client && !demoMode && result.clientId !== client.clientId) return false;
        return true;
      });
      return results.length ? [{ ...section, results }] : [];
    });
    const built: SearchSection[] = [
      ...(show("navigate") ? [{
        key: "pages",
        label: client ? `In ${displayName(client.clientName)}` : "Pages",
        results: searchClientPages(trimmed, ctx, queryParams).slice(0, MAX_PER_SECTION),
      }] : []),
      ...(show("navigate") ? [{
        key: "companies",
        label: "Companies",
        results: searchCompanies(trimmed, all, displayName, queryParams, client?.clientId)
          .filter((result) => !currentCompanyOnly || result.clientId === client?.clientId)
          .slice(0, MAX_PER_SECTION),
      }] : []),
      ...(show("navigate") && !currentCompanyOnly ? [{
        key: "agency",
        label: client ? "Elsewhere" : "Pages",
        results: searchAgencyPages(trimmed, ctx).slice(0, MAX_PER_SECTION),
      }] : []),
      // Records land between the destinations above and Help below: they are
      // more specific than a page but less likely than one to be the thing
      // being navigated to.
      ...entitySections,
      ...(show("help") && !currentCompanyOnly ? [{
        key: "help",
        label: "Help",
        results: searchDocTopics(trimmed, DOC_TOPICS, ctx).slice(0, MAX_PER_SECTION),
      }] : []),
      // Last, and only where the tool exists: an escape hatch for a query that
      // is a question rather than the name of something. Requires the company's
      // Deep Research toggle (which is what puts the provider in the list) and
      // a staff role, matching the page's own guard.
      ...(show("work") && client && isStaffRole(role) && client.providers.includes("deep_research")
        ? [{
            key: "ask",
            label: "Ask",
            results: [{
              id: "ask:deep-research",
              kind: "page" as const,
              title: `Ask Deep Research about "${trimmed}"`,
              subtitle: displayName(client.clientName),
              href: `/clients/${client.clientId}/integrations/deep-research?q=${encodeURIComponent(trimmed)}`,
              iconKey: "sparkles",
              matches: [],
              score: 0,
            }],
          }]
        : []),
    ];
    const seen = new Set<string>();
    return built.flatMap((section) => {
      const results = section.results.filter((result) => {
        const key = result.kind === "company"
          ? `company:${result.clientId ?? result.id}`
          : `${result.href}|${result.title.toLocaleLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return results.length ? [{ ...section, results }] : [];
    });
  }, [query, companies, recentClients, recentSearches, client, ctx, displayName, queryParams, entitySearch.data, role, category, currentCompanyOnly, demoMode]);

  // Arrow roving runs over one flat list; section headers are skipped because
  // they are not in it.
  const flat = useMemo(() => sections.flatMap((s) => s.results), [sections]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function go(result: SearchResult) {
    if (result.queryTerm) {
      setQuery(result.queryTerm);
      setActiveIndex(0);
      runEntitySearch(result.queryTerm);
      inputRef.current?.focus();
      return;
    }
    // Recorded on open, not on keystroke: a term that led somewhere is worth
    // offering back, a half-typed one that found nothing is not.
    recordSearch(query);
    closeSearch();
    if (/^https?:\/\//i.test(result.href)) window.location.assign(result.href);
    else router.push(result.href);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    // The sitemap builder binds bare keys on window and the tour binds arrows;
    // nothing typed into this input should reach either.
    e.stopPropagation();
    if (e.key === "Escape") {
      closeSearch();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length));
      return;
    }
    if (e.key === "Enter") {
      const target = flat[activeIndex];
      if (target) {
        e.preventDefault();
        go(target);
      }
    }
  }

  const hasQuery = query.trim().length >= MIN_QUERY;
  // True from the first keystroke until the records land, covering the debounce
  // window as well as the request. Mirrors the conditions runEntitySearch uses,
  // so the indicator never promises results that were never requested.
  const entitiesPending =
    entitySearch.pending && query.trim().length >= MIN_ENTITY_QUERY;

  const searchSurface = (
    <div
      ref={containerRef}
      data-tour="global-search"
      role={open && compactMode ? "dialog" : undefined}
      aria-modal={open && compactMode ? true : undefined}
      aria-label={open && compactMode ? "Search SPARK Dash" : undefined}
      className={open && compactMode
        ? "fixed inset-0 z-[120] flex h-[100dvh] flex-col bg-white text-spark-charcoal lg:hidden"
        : "relative w-full max-w-xl"
      }
    >
      {!open && (
        <button
          ref={mobileTriggerRef}
          type="button"
          onClick={openSearch}
          aria-label="Search SPARK Dash"
          className="flex h-11 w-11 items-center justify-center rounded-lg text-[#C3CAD3] transition-colors hover:bg-white/[.06] hover:text-white lg:hidden"
        >
          {icons.search}
        </button>
      )}

      <div className={compactMode && open ? "flex shrink-0 items-center gap-2 border-b border-spark-light-gray p-3" : "relative hidden lg:block"}>
        <div className="relative min-w-0 flex-1">
          <span aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8693]">
            {icons.search}
          </span>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls="global-search-results"
            aria-autocomplete="list"
            placeholder="Search SPARK Dash"
            value={query}
            onFocus={() => {
              setCompactMode(false);
              openSearch();
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              // Reset the highlight here rather than in an effect on `query`:
              // this handler is the only place the query changes, and an effect
              // would trip react-hooks/set-state-in-effect.
              setActiveIndex(0);
              runEntitySearch(e.target.value);
              if (!open) openSearch();
            }}
            onKeyDown={onKeyDown}
            className={compactMode
              ? "h-11 w-full rounded-lg border border-spark-border bg-spark-hover pl-9 pr-3 text-base text-spark-black placeholder:text-spark-gray focus:border-spark-blue focus:outline-none"
              : "h-8 w-full rounded-md border border-white/[.10] bg-white/[.06] pl-9 pr-16 text-sm text-white placeholder:text-[#7D8693] transition-colors hover:bg-white/[.09] focus:border-white/20 focus:bg-white/[.11] focus:outline-none"
            }
          />
          {!compactMode && (
            <kbd
              aria-hidden
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-white/[.14] px-1.5 py-0.5 text-[10px] font-medium text-[#7D8693]"
            >
              Ctrl K
            </kbd>
          )}
        </div>
        {compactMode && (
          <button
            type="button"
            onClick={closeSearch}
            aria-label="Close search"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-spark-gray transition-colors hover:bg-spark-hover hover:text-spark-black"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {open && (
        <div
          id="global-search-results"
          role="listbox"
          // Heavier than --shadow-card-hover on purpose: this panel floats over
          // arbitrary page content (tables, charts, dark chrome), not a neutral
          // card grid, so it needs a stronger break from whatever is under it.
          // Two layers - a soft ambient spread plus a tighter contact shadow -
          // read as more "lifted" than one large blur at the same weight.
          className={compactMode
            ? "static flex min-h-0 flex-1 flex-col overflow-hidden bg-white"
            : "absolute left-0 right-0 top-full z-[70] mt-2 overflow-hidden rounded-lg border border-spark-light-gray bg-white shadow-[0_24px_48px_-12px_rgba(16,24,40,0.28),0_8px_16px_-4px_rgba(16,24,40,0.16)]"
          }
        >
          <div className="flex flex-wrap items-center gap-1.5 border-b border-spark-light-gray px-3 py-2" aria-label="Search filters">
            {CATEGORY_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                aria-pressed={category === filter.value}
                onClick={() => {
                  setCategory(filter.value);
                  setActiveIndex(0);
                  runEntitySearch(query, { category: filter.value, currentCompanyOnly });
                }}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  category === filter.value
                    ? "bg-spark-blue text-white"
                    : "bg-spark-hover text-spark-gray hover:text-spark-black"
                }`}
              >
                {filter.label}
              </button>
            ))}
            {client && (
              <button
                type="button"
                aria-pressed={currentCompanyOnly}
                onClick={() => {
                  const next = !currentCompanyOnly;
                  setCurrentCompanyOnly(next);
                  setActiveIndex(0);
                  runEntitySearch(query, { category, currentCompanyOnly: next });
                }}
                className={`ml-auto rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  currentCompanyOnly
                    ? "border-spark-blue bg-spark-blue/10 text-spark-blue-text"
                    : "border-spark-light-gray text-spark-gray hover:text-spark-black"
                }`}
              >
                This company
              </button>
            )}
          </div>
          <div ref={listRef} className={compactMode ? "min-h-0 flex-1 overflow-y-auto py-1" : "max-h-[24rem] overflow-y-auto py-1"}>
            {sections.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-spark-gray">
                {!hasQuery
                  ? "Search companies, pages, and help"
                  : // Records arrive after the instant results, so "no matches"
                    // would be wrong (and would flash) while they are still on
                    // the way.
                    entitiesPending
                    ? "Searching..."
                    : `No matches for "${query.trim()}"`}
              </p>
            )}

            {sections.map((section) => (
              <div key={section.key}>
                <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[.07em] text-spark-gray">
                  {section.label}
                </p>
                {section.results.map((result) => {
                  const index = flat.indexOf(result);
                  const isActive = index === activeIndex;
                  return (
                    <button
                      key={result.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      data-index={index}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => go(result)}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                        isActive ? "bg-spark-hover" : ""
                      }`}
                    >
                      <span className="shrink-0 text-spark-gray">{icons[result.iconKey] ?? icons.grid}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="min-w-0 flex-1 truncate text-sm text-spark-black">
                          {highlight(result.title, result.matches).map((part, i) =>
                            part.bold ? (
                              <strong key={i} className="font-bold text-spark-blue-text">
                                {part.text}
                              </strong>
                            ) : (
                              <span key={i}>{part.text}</span>
                            ),
                          )}
                          </span>
                          {result.status && (
                            <span className="shrink-0 rounded-full bg-spark-hover px-1.5 py-0.5 text-[10px] font-semibold text-spark-gray">
                              {result.status.replaceAll("_", " ")}
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-xs text-spark-gray">{result.subtitle}</span>
                        {result.excerpt && (
                          <span className="mt-0.5 block truncate text-xs text-spark-blue-text">
                            {result.matchedField ? `${result.matchedField}: ` : ""}{result.excerpt}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Sits inside the scrolling list, below the last result, so it
                reads as "more is coming" rather than as a status bar. Not a
                [role=option] and not in `flat`, so arrow keys skip it. */}
            {sections.length > 0 && entitiesPending && (
              <p
                aria-live="polite"
                className="flex items-center gap-2 px-3 pb-2 pt-2.5 text-xs text-spark-gray"
              >
                <svg className="h-3 w-3 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Fetching more results...
              </p>
            )}
          </div>

          {compactMode ? (
            <div className="border-t border-spark-light-gray bg-spark-hover px-3 py-2 text-center text-[11px] text-spark-gray">
              Tap a result to open it
            </div>
          ) : (
            <div className="flex items-center gap-3 border-t border-spark-light-gray bg-spark-hover px-3 py-1.5 text-[11px] text-spark-gray">
              <span>
                <kbd className="font-sans font-semibold">↑↓</kbd> Select
              </span>
              <span>
                <kbd className="font-sans font-semibold">↵</kbd> Open
              </span>
              <span>
                <kbd className="font-sans font-semibold">Esc</kbd> Close
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (open && compactMode && typeof document !== "undefined") {
    return (
      <>
        <div data-tour="global-search" className="relative h-11 w-11 lg:hidden" />
        {createPortal(searchSurface, document.body)}
      </>
    );
  }

  return searchSurface;
}
